import { API_BASE_URL } from '../config/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
  };
};

interface FaceServiceStatus {
  face_recognition_available: boolean;
  service_initialized: boolean;
  message: string;
  required_packages?: string[];
  lightweight_packages?: string[];
  standard_packages?: string[];
  service_type?: string;
  model_info?: {
    name: string;
    description: string;
  };
}

interface FaceRegistrationResponse {
  success: boolean;
  message: string;
  data?: {
    user_id: string;
    registered_faces: number;
    face_files: string[];
  };
  error?: string;
  details?: any;
}

interface FaceIdentificationResponse {
  success: boolean;
  matched: boolean;
  user_id?: string;
  user_name?: string;
  member_number?: string;
  confidence?: number;
  message?: string;
}

export const faceRegistrationApi = {
  // 얼굴 인식 서비스 상태 확인
  checkStatus: async (): Promise<FaceServiceStatus> => {
    const response = await fetch(`/v1/status`);

    if (!response.ok) {
      throw new Error('서비스 상태 확인 실패');
    }

    return response.json();
  },

  // 얼굴 등록 (파일 업로드)
  registerFace: async (
    userId: string,
    userName: string,
    imageFiles: File[]
  ): Promise<FaceRegistrationResponse> => {
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('user_name', userName);

    // 여러 이미지 파일 추가
    imageFiles.forEach((file) => {
      formData.append('files', file);
    });

    const response = await fetch(`/v1/register`, {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      // 서비스 이용 불가 상태인 경우
      if (response.status === 503) {
        throw new Error('얼굴 인식 서비스가 설치되어 있지 않습니다. 관리자에게 문의하세요.');
      }
      throw new Error(data.detail?.message || data.detail || '얼굴 등록 실패');
    }

    return data;
  },

  // 얼굴 등록 (Base64 이미지)
  registerFaceBase64: async (
    userId: string,
    userName: string,
    imagesBase64: string[]
  ): Promise<FaceRegistrationResponse> => {
    const response = await fetch(`/v1/register-base64`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        user_name: userName,
        images_base64: imagesBase64,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('Face registration error:', data);
      if (response.status === 503) {
        throw new Error('얼굴 인식 서비스가 설치되어 있지 않습니다. 관리자에게 문의하세요.');
      }
      // Handle array of errors
      if (Array.isArray(data.detail)) {
        const errorMessages = data.detail.map((err: any) => err.msg || err.message || JSON.stringify(err)).join(', ');
        throw new Error(errorMessages);
      }
      throw new Error(data.detail?.message || data.detail || '얼굴 등록 실패');
    }

    return data;
  },

  // 얼굴 삭제
  deleteFace: async (userId: string): Promise<{ success: boolean; message: string }> => {
    try {

      const response = await fetch(`/v1/face-data/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });


      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ 얼굴 삭제 API 오류:', errorData);

        // 서비스 이용 불가 상태인 경우
        if (response.status === 503) {
          throw new Error('얼굴 인식 서비스가 설치되어 있지 않습니다. 관리자에게 문의하세요.');
        }

        // 사용자를 찾을 수 없는 경우
        if (response.status === 404) {
          const errorDetail = errorData.detail || '얼굴 데이터가 등록되어 있지 않습니다.';
          throw new Error(errorDetail);
        }

        // 서버 오류인 경우
        if (response.status === 500) {
          const errorMsg = errorData.detail || '서버에서 얼굴 삭제 중 오류가 발생했습니다.';
          throw new Error(errorMsg);
        }

        throw new Error(errorData.detail || `얼굴 삭제 실패 (${response.status})`);
      }

      const result = await response.json();

      return result;
      
    } catch (error) {
      console.error('💥 얼굴 삭제 오류:', error);
      throw error;
    }
  },

  // 얼굴 인식 테스트(identifyFace) 제거 — 2026-09-01
  //   서버가 얼굴을 판정하던 옛 주소(/v1/identify)를 부르던 함수다.
  //   판정은 태블릿이 혼자 하므로 서버 쪽 주소가 없어졌다.
  //   여기 남은 것들(등록·삭제·조회)은 계속 쓰인다.
};